import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import { accentBar, eyebrowPill } from '../../theme/mixins';

/**
 * Six tiles that reflow by available width rather than by breakpoint, so three across on a
 * tablet and one across on a phone need no separate rules.
 */
/**
 * Explicit counts rather than auto-fit. Six tiles reflowed by width land on whatever
 * divides awkwardly at the container's size -- five and a stray one, here -- whereas fixed
 * counts always split evenly: two rows of three on a tablet, one row of six on a desktop.
 */
export const kpiGridStyles: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: {
        xs: 'repeat(2, 1fr)',
        sm: 'repeat(3, 1fr)',
        lg: 'repeat(6, 1fr)',
    },
    gap: 2,
    mb: 3,
};

export const kpiCardStyles: SxProps<Theme> = {
    position: 'relative',
    // Tighter on a phone, where two tiles share 375px and the padding is the difference
    // between an amount fitting and being clipped.
    p: { xs: 2, sm: 2.5 },
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
};

/**
 * Only the income tile is accented -- if everything is emphasised, nothing is.
 *
 * Absolutely positioned along the card's top edge rather than sitting in the flow, so the
 * one accented tile still starts its label on the same line as the other five. In the flow
 * it pushed "Income" a bar's height below every other heading in the row.
 */
export const kpiAccentBarStyles: SxProps<Theme> = {
    ...accentBar,
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    width: 'auto',
    borderRadius: '12px 12px 0 0',
};

export const kpiLabelBoxStyles: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
};

export const kpiLabelStyles: SxProps<Theme> = {
    ...eyebrowPill,
    px: 0,
    mb: 0,
    backgroundColor: 'transparent',
    color: 'text.secondary',
};

/**
 * Tabular figures so the digits keep their columns when the income toggle swaps the value
 * underneath. A number that jitters as you flip between two readings of it invites the
 * reader to distrust both.
 */
export const kpiValueStyles: SxProps<Theme> = {
    fontWeight: 700,
    lineHeight: 1.2,
    // Smaller than h4's default so an amount and its currency stay on one line. It steps
    // down again at lg, where six tiles share the row and each is at its narrowest.
    // nowrap so an unexpectedly large figure overflows visibly rather than silently
    // making its tile taller than the five beside it.
    fontSize: { xs: '1.2rem', sm: '1.65rem', lg: '1.35rem' },
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
};

export const kpiCaptionStyles: SxProps<Theme> = {
    color: 'text.secondary',
};

export const kpiHelpIconStyles = (theme: Theme): SxProps<Theme> => ({
    fontSize: 16,
    color: theme.palette.grey[500],
    cursor: 'help',
});
